import {classNames} from "utils/helpers";
import {DragDropContext, DragDropHandler, DropTargetData, TargetHash} from "./drag-drop-context";
import "./drag-drop-container.less"
import { PropsWithChildren, useRef, useState } from "react";

interface DragDropContainerProps {
  className?: string
}

export function DragDropContainer(props: PropsWithChildren<DragDropContainerProps>) {
  const ref = useRef<HTMLElement|null>(null);
  const [state, setState] = useState({
    targets: {} as TargetHash,
    usedTouchIds: [] as number[]
  });
  
  const dragDropContext = {
    targets: state.targets,
    usedTouchIds: state.usedTouchIds,
    
    addTouchId(touchId: number): void {
      setState(prevState => {
        const idx = prevState.usedTouchIds.indexOf(touchId)
        if (idx === -1) {
          return {
            ...prevState,
            usedTouchIds: [
              ...prevState.usedTouchIds,
              touchId
            ]
          }
        } else {
          return prevState
        }
      })
    },
    
    removeTouchId(touchId: number): void {
      setState(prevState => {
        const usedTouchIds = prevState.usedTouchIds.slice().filter(x => x !== touchId);
        return {
          ...prevState,
          usedTouchIds
        }
      })
    },
    
    addTarget(target: DropTargetData) {
      // console.log('addTarget', target.id)
      setState(prevState => {
        const targets = {...prevState.targets}
        targets[target.id] = target
        return {
          ...prevState,
          targets,
        }
      })
    },
    removeTarget(id: string) {
      // console.log('removeTarget', id)
      setState(prevState => {
        const targets = {...prevState.targets}
        delete targets[id]
        return {
          ...prevState,
          ...targets
        }
      })
    },
    onDragOver(e: {itemId: string, targetId: string, onUserDragStart?: DragDropHandler}) {
      setState(prevState => {
        const target = prevState.targets[e.targetId];
        if (target) {
          
          const targets = {...prevState.targets};
          targets[e.targetId] = {
            ...target,
            isDraggedOver: true
          }

          const onUserDragStart = e.onUserDragStart;
          const onUserDragOver = target.onUserDragOver
          if (onUserDragOver && !target.isDraggedOver) {
            setTimeout(() => {
              // NOTE: always in correct order
              if (onUserDragStart) {
                onUserDragStart(e.itemId)  
              }
              onUserDragOver(e.itemId)
            })
          }
          
          return {
            ...prevState,
            targets
          }
        } else {
          
          // ALWAYS invoke drag-start if specified
          if (e.onUserDragStart) {
            const onUserDragStart = e.onUserDragStart;
            setTimeout(() => {
              onUserDragStart(e.itemId)
            })
          }

          return prevState
        }
      })
    },
    onDragExit(e: {itemId: string, targetId: string}) {
      setState(prevState => {
        const target = prevState.targets[e.targetId];
        if (target) {
          const onUserDragExit = target.onUserDragExit
          if (onUserDragExit && target.isDraggedOver) {
            setTimeout(() => onUserDragExit(e.itemId))
          }

          const targets = {...prevState.targets};
          targets[e.targetId] = {
            ...target,
            isDraggedOver: false
          }
          return {...prevState, targets}
        } else {
          return prevState
        }
      })
    },
    onDrop(e: {itemId: string, targetId: string}) {
      setState(prevState => {
        const target = prevState.targets[e.targetId]
        if (target) {
          const targets = {...prevState.targets}
          targets[e.targetId] = {
            ...target,
            isDraggedOver: false
          }
          
          const onUserDrop = target.onUserDrop;
          if (onUserDrop) {
            setTimeout(() => onUserDrop(e.itemId))
          }
          
          return {...prevState, targets}
        } else {
          return prevState;
        }
      })
    }
  }

  return <div
    className={classNames("drag-drop-container", props.className)}
  >
    <DragDropContext.Provider value={dragDropContext}>
      {props.children}
    </DragDropContext.Provider>
  </div>
}
